<template>
  <div v-if="variant !== 'inline'" class="relative min-h-10">
    <!-- content always rendered; overlay shows on top -->
    <slot />
    <div
      v-if="loading"
      class="absolute inset-0 grid place-items-center bg-white/50 backdrop-blur-[2px] pointer-events-none"
      aria-busy="true"
      aria-live="polite"
    >
      <div class="loader-shell" aria-hidden="true">
        <span class="loader" :class="sizeClass" :style="styleVars" />
      </div>
    </div>
  </div>

  <span
    v-else-if="loading"
    class="loader loader--inline"
    :class="sizeClass"
    :style="styleVars"
    aria-busy="true"
    aria-live="polite"
  />
</template>

<script setup>
const props = defineProps({
  loading: { type: Boolean, required: true },
  // keep for API compatibility; not needed with overlay approach
  withSlot: { type: Boolean, required: false, default: false },
  // overlay: wraps slot and shows overlay spinner; inline: renders only spinner
  variant: { type: String, required: false, default: 'overlay' },
  // xs/sm/md/lg
  size: { type: String, required: false, default: 'md' },
  // optional override (e.g. "#fff" on dark buttons)
  color: { type: String, required: false, default: '' },
});

const sizeClass = computed(() => `loader--${props.size}`);
const styleVars = computed(() => (props.color ? { '--loader-color': props.color } : undefined));
</script>

<style scoped>
/* A lightweight shell makes the loader pop on busy UIs */
.loader-shell {
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.65);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.10),
    0 2px 8px rgba(0, 0, 0, 0.08);
}

.loader {
  width: var(--loader-size, 34px);
  height: var(--loader-size, 34px);
  border-radius: 9999px;
  position: relative;

  /* Robust spinner (works even if advanced CSS features aren't supported) */
  --loader-color: var(--color-primary, #4CAF50);
  border: var(--loader-border, 3px) solid rgba(17, 24, 39, 0.15);
  border-top-color: var(--loader-color, var(--color-primary, #4CAF50));
  animation: spin 0.9s linear infinite;
  box-sizing: border-box;
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.12));
}

/* Inner ring rotating opposite direction */
.loader::after {
  content: "";
  position: absolute;
  inset: var(--loader-inner-inset, 7px);
  border-radius: inherit;
  border: var(--loader-inner-border, 2px) solid rgba(17, 24, 39, 0.10);
  border-right-color: var(--loader-color, var(--color-primary, #4CAF50));
  box-sizing: border-box;
  animation: spinReverse 0.7s linear infinite;
}

.loader--inline {
  display: inline-block;
  vertical-align: middle;
  filter: none;
}

.loader--xs {
  --loader-size: 16px;
  --loader-border: 2px;
  --loader-inner-inset: 4px;
  --loader-inner-border: 2px;
}

.loader--sm {
  --loader-size: 20px;
  --loader-border: 2px;
  --loader-inner-inset: 5px;
  --loader-inner-border: 2px;
}

.loader--md {
  --loader-size: 34px;
  --loader-border: 3px;
  --loader-inner-inset: 7px;
  --loader-inner-border: 2px;
}

.loader--lg {
  --loader-size: 44px;
  --loader-border: 3px;
  --loader-inner-inset: 9px;
  --loader-inner-border: 2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes spinReverse {
  to {
    transform: rotate(-360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .loader,
  .loader::after {
    animation: none;
  }
}
</style>
